import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Lock } from "lucide-react";

import Seo from "@/components/foundation/Seo";
import Button from "@/components/foundation/Button";
import GridPattern from "@/components/foundation/GridPattern";
import GlowOrb from "@/components/foundation/GlowOrb";
import Logo from "@/components/brand/Logo";
import { adminApi, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const TOKEN_KEY = "zenme_admin_token";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token.trim()) {
      setError("Please enter your admin token.");
      return;
    }
    setError("");
    setStatus("submitting");
    try {
      // Validate by calling overview endpoint
      await adminApi.overview(token.trim());
      sessionStorage.setItem(TOKEN_KEY, token.trim());
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      setStatus("idle");
      if (err instanceof ApiError && err.status === 401) {
        setError("Invalid admin token.");
      } else {
        setError(err?.message || "Login failed. Try again.");
      }
    }
  };

  return (
    <div className="relative min-h-screen bg-bg-base text-white">
      <Seo
        title="ZenMe Capital · Admin"
        description="Internal admin panel."
        path="/admin"
      />
      <GridPattern variant="dots" size={30} opacity={0.35} />
      <GlowOrb color="emerald" size={520} intensity={0.2} className="-bottom-40 left-1/2 -translate-x-1/2" />

      <div className="relative z-10 flex min-h-screen items-center justify-center px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md rounded-3xl border border-white/[0.06] bg-bg-card/95 p-8 backdrop-blur-xl sm:p-10"
          data-testid="admin-login-card"
        >
          <div className="flex items-center justify-between">
            <Logo height={26} />
            <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-emerald/25 bg-brand-emerald/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-brand-emerald">
              <Lock className="h-3 w-3" />
              Admin
            </span>
          </div>

          <h1 className="mt-8 font-display text-3xl font-semibold tracking-tight text-white">
            Sign in
          </h1>
          <p className="mt-2 text-sm text-text-secondary">
            Internal use only. Enter your admin token to continue.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-4" noValidate>
            <label className="flex flex-col">
              <span className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-text-muted">
                Admin Token
              </span>
              <input
                type="password"
                value={token}
                onChange={(e) => {
                  setToken(e.target.value);
                  if (error) setError("");
                }}
                autoComplete="off"
                aria-invalid={Boolean(error)}
                className={cn(
                  "w-full rounded-xl border bg-white/[0.02] px-4 py-3 font-mono text-sm text-white placeholder:text-text-muted outline-none transition-colors focus:border-brand-emerald/60 focus:bg-white/[0.04]",
                  error
                    ? "border-red-500/60"
                    : "border-white/[0.08] hover:border-white/[0.16]",
                )}
                placeholder="Bearer token"
                data-testid="admin-token-input"
              />
              {error && (
                <span
                  className="mt-1.5 pl-1 text-xs text-red-400"
                  role="alert"
                  data-testid="admin-login-error"
                >
                  {error}
                </span>
              )}
            </label>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={status === "submitting"}
              rightIcon={status === "submitting" ? null : <ArrowRight className="h-4 w-4" />}
              data-testid="admin-login-submit"
            >
              {status === "submitting" ? "Signing in…" : "Sign In"}
            </Button>
          </form>

          <p className="mt-6 text-[11px] text-text-muted">
            Warning: this area is restricted. All actions are logged.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
